// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0
import {
	DisplayFieldsResponse,
	SafeObjectChange,
	SafeObjectChangeCreated,
	SafeObjectChangeDeleted,
	SafeObjectChangeMutated,
	SafeObjectChangePublished,
	SafeObjectChangeTransferred,
	SafeObjectChangeWrapped,
} from '@safecoin/safe/jsonRpc';

import { groupByOwner } from './groupByOwner';
import { SafeObjectChangeTypes } from './types';

export type WithDisplayFields<T> = T & { display?: DisplayFieldsResponse };
export type SafeObjectChangeWithDisplay = WithDisplayFields<SafeObjectChange>;

export type ObjectChanges = {
	changesWithDisplay: SafeObjectChangeWithDisplay[];
	changes: SafeObjectChange[];
	ownerType: string;
};
export type ObjectChangesByOwner = Record<string, ObjectChanges>;

export type ObjectChangeSummary = {
	[K in SafeObjectChangeTypes]: ObjectChangesByOwner;
};

export const getObjectChangeSummary = (objectChanges: SafeObjectChangeWithDisplay[]) => {
	if (!objectChanges) return null;

	const mutated = objectChanges.filter(
		(change) => change.type === 'mutated',
	) as SafeObjectChangeMutated[];

	const created = objectChanges.filter(
		(change) => change.type === 'created',
	) as SafeObjectChangeCreated[];

	const transferred = objectChanges.filter(
		(change) => change.type === 'transferred',
	) as SafeObjectChangeTransferred[];

	const published = objectChanges.filter(
		(change) => change.type === 'published',
	) as SafeObjectChangePublished[];

	const wrapped = objectChanges.filter(
		(change) => change.type === 'wrapped',
	) as SafeObjectChangeWrapped[];

	const deleted = objectChanges.filter(
		(change) => change.type === 'deleted',
	) as SafeObjectChangeDeleted[];

	return {
		transferred: groupByOwner(transferred),
		created: groupByOwner(created),
		mutated: groupByOwner(mutated),
		published: groupByOwner(published),
		wrapped: groupByOwner(wrapped),
		deleted: groupByOwner(deleted),
	};
};
